import { TextFieldProps as MuiTextFieldProps } from '@nexelem/deprecated-theme';
import { IconComponentProps } from '@nexelem/icons';
import isEqual from 'date-fns/isEqual';
import { FC, ReactNode, useEffect, useMemo, useRef } from 'react';
import { useIMask } from 'react-imask';
import { v4 as uuidv4 } from 'uuid';
import { InputLabel } from '../InputLabel';
import { StyledTextField } from './StyledTextField';
import { NullableMaskedNumber } from './NullableMaskedNumber';
import { FactoryOpts, MaskedFunction, MaskedNumberOptions } from 'imask';
import { faCircleExclamation, faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

type InheritedTextFieldProps = Pick<
  MuiTextFieldProps,
  'label' | 'placeholder' | 'InputProps' | 'InputLabelProps' | 'required' | 'onBlur' | 'onFocus'
>;

export interface InternalFieldProps<T = string> extends InheritedTextFieldProps {
  readonly onChange?: (value: T) => void;
  readonly id?: string;
  readonly helperText?: ReactNode;
  readonly errorMessage?: ReactNode;
  readonly type?: 'text' | 'password' | 'number';
  readonly size?: 'medium' | 'small' | 'large' | 'xl' | 'jumbo';
  readonly className?: string;
  readonly value?: T;
  readonly error?: boolean;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly loading?: boolean;
  readonly fullWidth?: boolean;
  readonly showErrorBorder?: boolean;
  readonly HelperTextIcon?: FC<IconComponentProps>;
  readonly maskOptions?: FactoryOpts;
  readonly multiline?: boolean;
  readonly enableClearButton?: boolean;
  readonly onClear?: () => void;
  readonly textAlign?: 'left' | 'right' | 'center';
}

export function InternalField<T = string>({
  onChange,
  id,
  helperText,
  errorMessage,
  disabled,
  readOnly,
  type = 'text',
  size = 'medium',
  className,
  required,
  label,
  maskOptions,
  value: parentValue,
  multiline = false,
  textAlign = 'left',
  error,
  enableClearButton = false,
  onClear,
  ...props
}: InternalFieldProps<T>) {
  const mask = useMemo(() => {
    if (type === 'number') {
      return new NullableMaskedNumber((maskOptions as MaskedNumberOptions) ?? { mask: Number });
    }
    return maskOptions ?? { mask: new MaskedFunction({ mask: (str: string) => String(str) }), lazy: false };
  }, [maskOptions, type]);

  const internalId = useMemo(() => id ?? uuidv4(), [id]);
  const { ref, value, typedValue, maskRef } = useIMask(mask, {
    onAccept: (value, maskRef) => {
      onChange?.(maskRef.typedValue as T);
    },
    onComplete(value, maskRef) {
      onChange?.(maskRef.typedValue as T);
    },
  });

  const labelRef = useRef(null);

  useEffect(() => {
    if (
      !maskRef.current ||
      parentValue === typedValue ||
      (typedValue instanceof Date && parentValue instanceof Date && isEqual(typedValue, parentValue))
    ) {
      return;
    }

    const isNumberNonEmptyValue = typeof parentValue === 'number';
    const isNonEmptyValue = (typeof parentValue === 'string' || parentValue instanceof Date) && parentValue;

    if (isNumberNonEmptyValue || isNonEmptyValue) {
      maskRef.current.typedValue = parentValue;
      maskRef.current.updateValue();
    }
    // we don't want to watch typedValue for changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maskRef, parentValue]);

  useEffect(() => {
    if (maskRef.current && !parentValue && parentValue !== 0) {
      maskRef.current.typedValue = '';
      maskRef.current.updateValue();
    }
  }, [type, maskRef, parentValue]);

  const isSelectWithList = !!props.InputProps?.ref;

  const isJumboSize = size === 'jumbo';

  return (
    <>
      <div className="relative">
        <StyledTextField
          {...props}
          className={className}
          disabled={readOnly || disabled}
          error={error}
          id={internalId}
          inputRef={isSelectWithList ? null : ref}
          label={label ? <InputLabel isJumbo={isJumboSize} label={label} ref={labelRef} required={required} /> : null}
          maxRows={6}
          multiline={multiline}
          readOnly={readOnly}
          size={size}
          textAlign={textAlign}
          type={type === 'number' ? 'text' : type}
          value={isSelectWithList ? null : value}
          variant="outlined"
          withEndIcon={!!props.InputProps?.endAdornment}
          withStartIcon={!!props.InputProps?.startAdornment}
          InputLabelProps={{
            ...(props.InputLabelProps || {}),
            shrink: false,
          }}
        />
      </div>
      {!!((error && errorMessage) || helperText) && (
        <div className={clsx('mt-2 ml-2 flex items-center gap-2 text-t14-sb')}>
          <span className="flex h-5 w-5 items-center justify-center">
            {errorMessage ? (
              <FontAwesomeIcon className="text-icon-error" icon={faCircleExclamation} />
            ) : (
              <FontAwesomeIcon className="text-icon-primary" icon={faCircleInfo} />
            )}
          </span>
          <span className={clsx(errorMessage ? 'text-text-state-error-light' : 'text-text-secondary')}>
            {errorMessage || helperText}
          </span>
        </div>
      )}
    </>
  );
}
